import { useCallback, useMemo, type FC } from 'react'
import classNames from 'classnames'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Text } from 'components'
import { services, getService, resolveLang, type TServiceBlock } from '../ServicesPage/servicesCatalog'

import styles from './ServiceDetail.module.scss'

const SITE = 'https://www.engineeringgroup.am'
const BRAND = 'ENGINEERING GROUP'

type TSection = {
  title: string
  lead?: string
  items: TServiceBlock[]
}

const buildSections = (blocks: TServiceBlock[]) => {
  const intro: TServiceBlock[] = []
  const sections: TSection[] = []
  let current: TSection | null = null

  blocks.forEach(block => {
    if (block.type === 'step') {
      current = { title: block.text, lead: block.lead, items: [] }
      sections.push(current)
    } else if (current) {
      current.items.push(block)
    } else {
      intro.push(block)
    }
  })

  return { intro, sections }
}


const Bullets: FC<{ items: TServiceBlock[] }> = ({ items }) => {
  const groups: Array<{ type: 'list' | 'block'; items: TServiceBlock[] }> = []
  items.forEach(item => {
    if (item.type === 'bullet') {
      const last = groups[groups.length - 1]
      if (last && last.type === 'list') last.items.push(item)
      else groups.push({ type: 'list', items: [item] })
    } else {
      groups.push({ type: 'block', items: [item] })
    }
  })

  return (
    <>
      {groups.map((group, i) =>
        group.type === 'list' ? (
          <ul key={i} className={styles.bullets}>
            {group.items.map((b, j) => (
              <li key={j} className={styles.bullets__item}>
                {b.text}
              </li>
            ))}
          </ul>
        ) : group.items[0].type === 'sub' ? (
          <h4 key={i} className={styles.subhead}>
            {group.items[0].text}
          </h4>
        ) : (
          <p key={i} className={styles.para}>
            {group.items[0].text}
          </p>
        )
      )}
    </>
  )
}

const ServiceDetail: FC = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = resolveLang(i18n.language)

  const service = getService(slug)

  const index = useMemo(() => services.findIndex(s => s.slug === slug), [slug])
  const prev = index > 0 ? services[index - 1] : undefined
  const next = index >= 0 && index < services.length - 1 ? services[index + 1] : undefined

  const handleBack = useCallback(() => navigate('/services'), [navigate])
  const handleContact = useCallback(() => navigate('/contact-us'), [navigate])

  if (!service) {
    return (
      <div className={styles.missing}>
        <Text text='servicesPageIntro' className={styles.missing__text} tagName='h1' />
        <button className={styles.backBtn} onClick={handleBack}>
          <span className={styles.backBtn__arrow} aria-hidden />
          <Text text='services' />
        </button>
      </div>
    )
  }

  const { Icon } = service
  const title = service.titles[lang] || service.titles.am
  const { intro, sections } = buildSections(service.content[lang] || service.content.am || [])
  const introText = intro.filter(b => b.type === 'para').map(b => b.text)
  const introBullets = intro.filter(b => b.type !== 'para')

  const metaTitle = `${title} | ${BRAND}`
  const metaDescription = (introText.join(' ') || title).slice(0, 300)

  const pageUrl = `${SITE}/services/${service.slug}`
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        name: title,
        serviceType: t(service.category),
        description: metaDescription,
        url: pageUrl,
        areaServed: { '@type': 'Country', name: 'Armenia' },
        provider: {
          '@type': 'Organization',
          name: BRAND,
          url: SITE,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('home'), item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: t('services'), item: `${SITE}/services` },
          { '@type': 'ListItem', position: 3, name: title, item: pageUrl },
        ],
      },
    ],
  }

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name='description' content={metaDescription} />
        <meta property='og:title' content={metaTitle} />
        <meta property='og:description' content={metaDescription} />
        <meta property='og:url' content={pageUrl} />
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>

      <section className={styles.hero}>
        <div className={classNames('container', styles.hero__inner)}>
          <button className={styles.backBtn} onClick={handleBack}>
            <span className={styles.backBtn__arrow} aria-hidden />
            <Text text='services' />
          </button>

          <div className={styles.hero__head}>
            <span className={styles.hero__iconWrap} aria-hidden>
              <Icon className={styles.hero__icon} />
            </span>
            <div className={styles.hero__headText}>
              <Text text={service.category} className={styles.hero__eyebrow} />
              <h1 className={styles.hero__title}>{title}</h1>
            </div>
          </div>

          {!!introText.length && (
            <p className={styles.hero__lead}>{introText.join(' ')}</p>
          )}
        </div>
      </section>

      <div className={classNames('container', styles.body)}>
        {!!introBullets.length && (
          <div className={styles.introCard}>
            <Bullets items={introBullets} />
          </div>
        )}

        <ol className={styles.steps} key={slug}>
          {sections.map((section, i) => (
            <li className={styles.step} key={i} style={{ animationDelay: `${i * 70}ms` }}>
              <div className={styles.step__marker} aria-hidden>
                <span className={styles.step__num}>{String(i + 1).padStart(2, '0')}</span>
              </div>
              <div className={styles.step__card}>
                <h3 className={styles.step__title}>{section.title}</h3>
                {section.lead && <p className={styles.step__lead}>{section.lead}</p>}
                {!!section.items.length && <Bullets items={section.items} />}
              </div>
            </li>
          ))}
        </ol>

        <div className={styles.cta}>
          <Text text='ctaTitle' className={styles.cta__title} tagName='h2' />
          <button className={styles.cta__button} onClick={handleContact}>
            <Text text='contactUs' />
            <span className={styles.cta__arrow} aria-hidden />
          </button>
        </div>

        <nav className={styles.pager} aria-label='Services'>
          {prev ? (
            <button className={styles.pager__link} onClick={() => navigate(`/services/${prev.slug}`)}>
              <span className={styles.pager__dir}>
                <span className={classNames(styles.pager__chev, styles.pager__chev_prev)} aria-hidden />
                <Text text='servicePrev' />
              </span>
              <span className={styles.pager__name}>{prev.titles[lang] || prev.titles.am}</span>
            </button>
          ) : (
            <span />
          )}
          {next ? (
            <button className={classNames(styles.pager__link, styles.pager__link_next)} onClick={() => navigate(`/services/${next.slug}`)}>
              <span className={styles.pager__dir}>
                <Text text='serviceNext' />
                <span className={styles.pager__chev} aria-hidden />
              </span>
              <span className={styles.pager__name}>{next.titles[lang] || next.titles.am}</span>
            </button>
          ) : (
            <span />
          )}
        </nav>
      </div>
    </div>
  )
}

export default ServiceDetail
